import axios from "axios";
import { clearAuth, getToken, getStoredUser, getSelectedAccountId } from "@/lib/auth";
import { toast } from "@/hooks/use-toast";


const BACKEND_URL = window.location.origin;
const API_BASE = `${BACKEND_URL}/api`;

const storePostLoginRedirect = () => {
  if (typeof window === "undefined") return;
  const dest = window.location.pathname + window.location.search;
  if (dest.startsWith("/app")) {
    sessionStorage.setItem("post_login_redirect", dest);
  }
};

/**
 * P0 TENANT ISOLATION: Get tenant_id from URL query params
 * This is the source of truth for SuperAdmin tenant context
 */
const getTenantIdFromUrl = () => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("tenant_id") || null;
};

/**
 * P0 TENANT ISOLATION: Check if current user is SuperAdmin
 */
const isSuperAdmin = () => {
  const user = getStoredUser();
  return user?.role === "super_admin";
};

export const api = axios.create({
  baseURL: API_BASE,
});

/**
 * P0 TENANT ISOLATION: Request interceptor that handles tenant_id and account context
 * 
 * MA-PHASE11A-P0-FRONTEND-ACCOUNT-CONTEXT-HEADER-001:
 * Automatically attach X-Account-ID header for account-scoped requests
 * 
 * MA-PHASE11A-LOGIN-STUCK-REGRESSION-020:
 * EXCLUDE auth endpoints from X-Account-ID header (they establish context, don't consume it)
 * 
 * CRITICAL SAFETY RULES:
 * 1. Only SuperAdmins can send tenant_id query param overrides
 * 2. Non-SuperAdmins: Strip any tenant_id from params (safety measure)
 * 3. All authenticated users: Attach X-Account-ID header with selected account
 * 4. SuperAdmins: Auto-append tenant_id from URL if present (legacy support)
 * 5. Auth endpoints: Never receive X-Account-ID (they establish the context)
 */
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
    
    // MA-PHASE11A-LOGIN-STUCK-REGRESSION-020: Exclude auth endpoints from X-Account-ID
    // Auth endpoints establish account context, they don't consume it
    const isAuthEndpoint = config.url?.includes('/auth/');
    
    // P4-WP-3: EVERY authenticated user sends the selected Account.
    //
    // Before WP-3 this header was restricted to SuperAdmins, so an ordinary
    // multi-Account user's switch reached only /auth/me/access-context as a
    // query param -- their navigation changed while every data endpoint kept
    // serving their home Account. Account switching was cosmetic.
    //
    // Sending the header is NOT a grant. The backend
    // (core/unified_account_context) independently validates that the caller
    // holds an active direct membership in the named Account, or holds
    // platform authority, and refuses otherwise. Selected Account is request
    // context, never identity.
    const selectedAccountId = getSelectedAccountId();

    if (selectedAccountId && !isAuthEndpoint) {
      config.headers['X-Account-ID'] = selectedAccountId;
    }
  }
  
  // P0 TENANT ISOLATION: Handle tenant_id in requests (SuperAdmin only)
  const superAdmin = isSuperAdmin();
  const urlTenantId = getTenantIdFromUrl();
  
  // Initialize params if not present
  config.params = config.params || {};
  
  if (!superAdmin) {
    // SAFETY: Non-SuperAdmins MUST NOT send tenant_id overrides
    // Strip any tenant_id that may have been accidentally included
    if (config.params.tenant_id) {
      // console.warn("[API] Non-SuperAdmin attempted tenant_id override - stripped from request");
      delete config.params.tenant_id;
    }
  } else if (urlTenantId) {
    // SuperAdmin with URL tenant context: Auto-append tenant_id if not already set
    // This allows explicit overrides while providing automatic context propagation
    if (!config.params.tenant_id) {
      config.params.tenant_id = urlTenantId;
    }
  }
  
  // Clean up empty params object
  if (Object.keys(config.params).length === 0) {
    delete config.params;
  }
  
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    // IMPORTANT: do not clear auth on general errors; only explicit auth failures.
    if (status === 401) {
      const token = getToken();
      if (token) {
        storePostLoginRedirect();
        clearAuth();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }

    if (status === 403 && detail === "Account disabled") {
      // Immediately log out disabled users.
      storePostLoginRedirect();
      clearAuth();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    // Prevent React runtime overlay on transient rate limiting.
    // Individual pages should still handle errors, but this avoids a full crash.
    if (status === 429) {
      const message = "Too many requests. Please wait a moment and try again.";
      toast({
        title: "Rate limited",
        description: message,
        variant: "destructive",
      });
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

